/**
 * Household onboarding wizard — multi-step overlay for new users.
 * Steps: Welcome -> Create Household -> Add Box -> Attach Marker -> Add Items.
 */

import React, { useState, useCallback } from 'react';
import { createHousehold } from '../api/household';
import { createBox, addItem } from '../api/boxes';
import { getQrSvgUrl, getArucoSvgUrl } from '../api/markers';
import { useToast } from '../store/ToastContext';
import { ApiClientError } from '../api/boxes';

interface HouseholdOnboardingWizardProps {
  onComplete: () => void;
  onSkip: () => void;
}

interface OnboardingState {
  step: number;
  householdId: string | null;
  boxId: string | null;
  markerId: string;
  markerType: 'aruco' | 'qr';
  householdName: string;
  boxName: string;
  boxLocation: string;
  boxNotes: string;
  items: Array<{ name: string; quantity: number }>;
  loading: boolean;
}

const TOTAL_STEPS = 5;

export function HouseholdOnboardingWizard({ onComplete, onSkip }: HouseholdOnboardingWizardProps) {
  const { addToast } = useToast();
  const [state, setState] = useState<OnboardingState>({
    step: 0,
    householdId: null,
    boxId: null,
    markerId: crypto.randomUUID(),
    markerType: 'qr',
    householdName: '',
    boxName: '',
    boxLocation: '',
    boxNotes: '',
    items: [],
    loading: false,
  });

  const updateState = useCallback((partial: Partial<OnboardingState>) => {
    setState((s) => ({ ...s, ...partial }));
  }, []);

  const handleNext = useCallback(async () => {
    // Step 1: Create Household
    if (state.step === 1) {
      if (!state.householdName.trim()) {
        addToast('error', 'Please enter a household name');
        return;
      }

      updateState({ loading: true });
      try {
        const household = await createHousehold({ name: state.householdName.trim() });
        updateState({ householdId: household.id, loading: false, step: state.step + 1 });
        addToast('success', 'Household created!');
      } catch (err) {
        updateState({ loading: false });
        const message = err instanceof ApiClientError ? err.message : 'Failed to create household';
        addToast('error', message);
      }
      return;
    }

    // Step 2: Validate box data (don't create yet)
    if (state.step === 2) {
      if (!state.boxName.trim()) {
        addToast('error', 'Please enter a box name');
        return;
      }
      updateState({ step: state.step + 1 });
      return;
    }

    // Step 3: Create box with marker
    if (state.step === 3) {
      updateState({ loading: true });
      try {
        const box = await createBox({
          markerType: state.markerType,
          markerId: state.markerType === 'qr' ? state.markerId : state.markerId.toString(),
          name: state.boxName.trim(),
          location: state.boxLocation.trim() || undefined,
          notes: state.boxNotes.trim() || undefined,
        });
        updateState({ boxId: box.id, loading: false, step: state.step + 1 });
        addToast('success', 'Box created!');
      } catch (err) {
        updateState({ loading: false });
        const message = err instanceof ApiClientError ? err.message : 'Failed to create box';
        addToast('error', message);

        // If duplicate marker ID, regenerate
        if (err instanceof ApiClientError && err.statusCode === 409) {
          updateState({ markerId: crypto.randomUUID() });
          addToast('warning', 'Marker ID conflict, please try again with the new ID');
        }
      }
      return;
    }

    // Step 4: Finish (items are added in real-time)
    if (state.step === 4) {
      onComplete();
      return;
    }

    // Default: just advance
    if (state.step < TOTAL_STEPS - 1) {
      updateState({ step: state.step + 1 });
    }
  }, [state, onComplete, addToast, updateState]);

  const handleBack = useCallback(() => {
    if (state.step > 0) {
      updateState({ step: state.step - 1 });
    }
  }, [state.step, updateState]);

  const handleSkip = useCallback(async () => {
    // Create minimal household on skip
    updateState({ loading: true });
    try {
      await createHousehold({ name: 'My Household' });
      addToast('success', 'Household created! You can complete setup later from Settings.');
      onSkip();
    } catch (err) {
      updateState({ loading: false });
      const message = err instanceof ApiClientError ? err.message : 'Failed to create household';
      addToast('error', message);
    }
  }, [onSkip, addToast, updateState]);

  const handleAddItem = useCallback(
    async (name: string, quantity: number) => {
      if (!state.boxId) return;

      updateState({ loading: true });
      try {
        await addItem(state.boxId, { name, quantity });
        updateState({
          items: [...state.items, { name, quantity }],
          loading: false,
        });
        addToast('success', 'Item added!');
      } catch (err) {
        updateState({ loading: false });
        const message = err instanceof ApiClientError ? err.message : 'Failed to add item';
        addToast('error', message);
      }
    },
    [state.boxId, state.items, addToast, updateState],
  );

  const canGoNext = useCallback(() => {
    if (state.loading) return false;
    if (state.step === 1 && !state.householdName.trim()) return false;
    if (state.step === 2 && !state.boxName.trim()) return false;
    return true;
  }, [state]);

  return (
    <div
      data-testid="household-onboarding-wizard"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm"
    >
      <div className="w-full max-w-md mx-4 flex flex-col items-center px-6 py-10">
        {/* Step content */}
        {state.step === 0 && <WelcomeStep />}
        {state.step === 1 && (
          <CreateHouseholdStep
            householdName={state.householdName}
            onChange={(name) => updateState({ householdName: name })}
            loading={state.loading}
          />
        )}
        {state.step === 2 && (
          <CreateBoxStep
            boxName={state.boxName}
            location={state.boxLocation}
            notes={state.boxNotes}
            onBoxNameChange={(name) => updateState({ boxName: name })}
            onLocationChange={(location) => updateState({ boxLocation: location })}
            onNotesChange={(notes) => updateState({ boxNotes: notes })}
          />
        )}
        {state.step === 3 && (
          <AttachMarkerStep
            markerType={state.markerType}
            markerId={state.markerId}
            boxName={state.boxName}
            onMarkerTypeChange={(type) =>
              updateState({
                markerType: type,
                markerId: type === 'qr' ? crypto.randomUUID() : '0',
              })
            }
            onMarkerIdChange={(id) => updateState({ markerId: id })}
            loading={state.loading}
          />
        )}
        {state.step === 4 && (
          <AddItemsStep items={state.items} onAddItem={handleAddItem} loading={state.loading} />
        )}

        {/* Navigation */}
        <div className="mt-10 flex items-center gap-4 w-full max-w-xs">
          {state.step > 0 && state.step < 4 && (
            <button
              type="button"
              onClick={handleBack}
              disabled={state.loading}
              className="rounded-lg border border-border px-4 py-2.5 text-sm text-text-secondary hover:bg-surface transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Back
            </button>
          )}

          <button
            type="button"
            onClick={handleNext}
            disabled={!canGoNext()}
            className="flex-1 rounded-lg bg-accent px-4 py-2.5 text-sm font-semibold text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="household-onboarding-next"
          >
            {state.loading
              ? 'Loading...'
              : state.step === TOTAL_STEPS - 1
                ? 'Finish Setup'
                : 'Next'}
          </button>
        </div>

        {/* Skip */}
        <button
          type="button"
          onClick={handleSkip}
          disabled={state.loading}
          className="mt-4 text-xs text-text-secondary/60 hover:text-text-secondary transition-colors disabled:opacity-50"
          data-testid="household-onboarding-skip"
        >
          Skip for now
        </button>

        {/* Step dots */}
        <div className="mt-6 flex gap-2" aria-label={`Step ${state.step + 1} of ${TOTAL_STEPS}`}>
          {Array.from({ length: TOTAL_STEPS }, (_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === state.step
                  ? 'w-6 bg-accent'
                  : i < state.step
                    ? 'w-1.5 bg-accent/40'
                    : 'w-1.5 bg-border'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Step Components ────────────────────────────────────────────

function WelcomeStep() {
  return (
    <div className="text-center">
      {/* Home/Household icon */}
      <div className="mb-6 text-accent" aria-hidden="true">
        <svg
          className="h-20 w-20 mx-auto"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
          />
        </svg>
      </div>
      <h1 className="text-2xl font-bold text-text-primary">Welcome to Peekbox</h1>
      <p className="mt-3 text-sm text-text-secondary leading-relaxed max-w-xs mx-auto">
        Let&apos;s get you set up! We&apos;ll create your household, add your first box, attach a
        marker, and add some items. It only takes a minute.
      </p>
    </div>
  );
}

interface CreateHouseholdStepProps {
  householdName: string;
  onChange: (name: string) => void;
  loading: boolean;
}

function CreateHouseholdStep({ householdName, onChange, loading }: CreateHouseholdStepProps) {
  return (
    <div className="text-center w-full">
      {/* Household icon */}
      <div className="mb-6 text-accent" aria-hidden="true">
        <svg
          className="h-20 w-20 mx-auto"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z"
          />
        </svg>
      </div>
      <h1 className="text-2xl font-bold text-text-primary">Create Your Household</h1>
      <p className="mt-3 text-sm text-text-secondary leading-relaxed max-w-xs mx-auto">
        Your household is where all your boxes and items will live. Give it a name like
        &quot;Home&quot; or &quot;Office&quot;.
      </p>

      <div className="mt-6 w-full max-w-xs mx-auto">
        <label htmlFor="household-name" className="sr-only">
          Household Name
        </label>
        <input
          id="household-name"
          type="text"
          placeholder="e.g., Home"
          value={householdName}
          onChange={(e) => onChange(e.target.value)}
          disabled={loading}
          className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary/40 focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none disabled:opacity-50"
          autoFocus
        />
      </div>
    </div>
  );
}

interface CreateBoxStepProps {
  boxName: string;
  location: string;
  notes: string;
  onBoxNameChange: (name: string) => void;
  onLocationChange: (location: string) => void;
  onNotesChange: (notes: string) => void;
}

function CreateBoxStep({
  boxName,
  location,
  notes,
  onBoxNameChange,
  onLocationChange,
  onNotesChange,
}: CreateBoxStepProps) {
  return (
    <div className="text-center w-full">
      {/* Box icon */}
      <div className="mb-6 text-accent" aria-hidden="true">
        <svg
          className="h-20 w-20 mx-auto"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
          />
        </svg>
      </div>
      <h1 className="text-2xl font-bold text-text-primary">Add Your First Box</h1>
      <p className="mt-3 text-sm text-text-secondary leading-relaxed max-w-xs mx-auto">
        Give your box a name and optionally add a location. You can always edit this later.
      </p>

      <div className="mt-6 w-full max-w-xs mx-auto space-y-3">
        <div>
          <label htmlFor="box-name" className="block text-left text-xs text-text-secondary mb-1">
            Box Name *
          </label>
          <input
            id="box-name"
            type="text"
            placeholder="e.g., Living Room Storage"
            value={boxName}
            onChange={(e) => onBoxNameChange(e.target.value)}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary/40 focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
            autoFocus
          />
        </div>

        <div>
          <label
            htmlFor="box-location"
            className="block text-left text-xs text-text-secondary mb-1"
          >
            Location (optional)
          </label>
          <input
            id="box-location"
            type="text"
            placeholder="e.g., Garage, Attic"
            value={location}
            onChange={(e) => onLocationChange(e.target.value)}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary/40 focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
          />
        </div>

        <div>
          <label htmlFor="box-notes" className="block text-left text-xs text-text-secondary mb-1">
            Notes (optional)
          </label>
          <textarea
            id="box-notes"
            placeholder="e.g., Seasonal decorations"
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary/40 focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none resize-none"
          />
        </div>
      </div>
    </div>
  );
}

interface AttachMarkerStepProps {
  markerType: 'aruco' | 'qr';
  markerId: string;
  boxName: string;
  onMarkerTypeChange: (type: 'aruco' | 'qr') => void;
  onMarkerIdChange: (id: string) => void;
  loading: boolean;
}

function AttachMarkerStep({
  markerType,
  markerId,
  boxName,
  onMarkerTypeChange,
  onMarkerIdChange,
  loading,
}: AttachMarkerStepProps) {
  const markerUrl =
    markerType === 'qr'
      ? getQrSvgUrl(markerId, 256, boxName)
      : getArucoSvgUrl(parseInt(markerId) || 0, 256, boxName);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = markerUrl;
    link.download = `${boxName.replace(/[^a-zA-Z0-9]/g, '-')}-marker.svg`;
    link.click();
  };

  return (
    <div className="text-center w-full">
      {/* QR/Marker icon */}
      <div className="mb-6 text-accent" aria-hidden="true">
        <svg
          className="h-20 w-20 mx-auto"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6.75 6.75h.75v.75h-.75v-.75zM6.75 16.5h.75v.75h-.75v-.75zM16.5 6.75h.75v.75h-.75v-.75zM13.5 13.5h.75v.75h-.75v-.75zM13.5 19.5h.75v.75h-.75v-.75zM19.5 13.5h.75v.75h-.75v-.75zM19.5 19.5h.75v.75h-.75v-.75zM16.5 16.5h.75v.75h-.75v-.75z"
          />
        </svg>
      </div>
      <h1 className="text-2xl font-bold text-text-primary">Attach a Marker</h1>
      <p className="mt-3 text-sm text-text-secondary leading-relaxed max-w-xs mx-auto">
        This marker will help you identify your box with your camera. Print it and attach it to your
        box.
      </p>

      <div className="mt-6 w-full max-w-xs mx-auto space-y-4">
        <div>
          <label htmlFor="marker-type" className="block text-left text-xs text-text-secondary mb-1">
            Marker Type
          </label>
          <select
            id="marker-type"
            value={markerType}
            onChange={(e) => onMarkerTypeChange(e.target.value as 'aruco' | 'qr')}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
          >
            <option value="qr">QR Code (recommended)</option>
            <option value="aruco">ArUco Marker</option>
          </select>
        </div>

        {markerType === 'aruco' && (
          <div>
            <label htmlFor="aruco-id" className="block text-left text-xs text-text-secondary mb-1">
              ArUco ID (0-49)
            </label>
            <input
              id="aruco-id"
              type="number"
              min="0"
              max="49"
              value={markerId}
              onChange={(e) => onMarkerIdChange(e.target.value)}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none"
            />
          </div>
        )}

        {/* Marker Preview */}
        <div className="p-4 bg-white rounded-lg">
          <img src={markerUrl} alt="Marker preview" className="w-full h-auto" />
        </div>

        <button
          type="button"
          onClick={handleDownload}
          disabled={loading}
          className="w-full rounded-lg border border-border bg-surface px-4 py-2 text-sm text-text-primary hover:bg-border transition-colors disabled:opacity-50"
        >
          Download Marker
        </button>
      </div>
    </div>
  );
}

interface AddItemsStepProps {
  items: Array<{ name: string; quantity: number }>;
  onAddItem: (name: string, quantity: number) => void;
  loading: boolean;
}

function AddItemsStep({ items, onAddItem, loading }: AddItemsStepProps) {
  const [itemName, setItemName] = useState('');
  const [quantity, setQuantity] = useState(1);

  const handleAdd = () => {
    if (!itemName.trim()) return;
    onAddItem(itemName.trim(), quantity);
    setItemName('');
    setQuantity(1);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && itemName.trim()) {
      handleAdd();
    }
  };

  return (
    <div className="text-center w-full">
      {/* Items/List icon */}
      <div className="mb-6 text-accent" aria-hidden="true">
        <svg
          className="h-20 w-20 mx-auto"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
          />
        </svg>
      </div>
      <h1 className="text-2xl font-bold text-text-primary">Add Items</h1>
      <p className="mt-3 text-sm text-text-secondary leading-relaxed max-w-xs mx-auto">
        Add some items to your box. You can always add more later.
      </p>

      <div className="mt-6 w-full max-w-xs mx-auto space-y-4">
        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Item name"
            value={itemName}
            onChange={(e) => setItemName(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading}
            className="flex-1 rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary/40 focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none disabled:opacity-50"
            autoFocus
          />
          <input
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
            disabled={loading}
            className="w-16 rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none disabled:opacity-50"
          />
          <button
            type="button"
            onClick={handleAdd}
            disabled={!itemName.trim() || loading}
            className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add
          </button>
        </div>

        {/* Items list */}
        {items.length > 0 && (
          <div className="mt-4 space-y-2 max-h-40 overflow-y-auto">
            {items.map((item, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between text-left bg-surface rounded px-3 py-2"
              >
                <span className="text-sm text-text-primary">{item.name}</span>
                <span className="text-xs text-text-secondary">×{item.quantity}</span>
              </div>
            ))}
          </div>
        )}

        {items.length === 0 && (
          <p className="text-xs text-text-secondary/60">No items yet. Add your first item above!</p>
        )}
      </div>
    </div>
  );
}
